import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

/**
 * AiMessageBubble — renders a single chat turn entry.
 *
 * role: 'user' | 'assistant' | 'error'
 * Stores the pre-op snapshot inside the transcript entry so undo is self-contained.
 */
function AiMessageBubble({ entry, onUndo, intl }) {
    const { role, text, modelSummary, changesSummary, undone, isError, errorType } = entry;

    const isUser = role === 'user';

    // ── User bubble ──────────────────────────────────────────────────────────
    if (isUser) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: 8,
                }}
            >
                <div
                    style={{
                        maxWidth: '80%',
                        backgroundColor: '#e8f0fe',
                        border: '1px solid #c2d0f5',
                        borderRadius: '12px 12px 4px 12px',
                        padding: '8px 12px',
                        fontSize: '0.85rem',
                        color: '#212529',
                        wordBreak: 'break-word',
                    }}
                >
                    {text}
                </div>
            </div>
        );
    }

    // ── Error bubble ─────────────────────────────────────────────────────────
    if (isError) {
        const bgColour = errorType === 'rejected' ? '#fff3cd' : '#f8d7da';
        const borderColour = errorType === 'rejected' ? '#ffc107' : '#f5c6cb';
        return (
            <div
                style={{ marginBottom: 8 }}
                role="alert"
            >
                <div
                    style={{
                        maxWidth: '90%',
                        backgroundColor: bgColour,
                        border: `1px solid ${borderColour}`,
                        borderRadius: '4px 12px 12px 4px',
                        padding: '8px 12px',
                        fontSize: '0.85rem',
                        color: '#212529',
                        wordBreak: 'break-word',
                    }}
                >
                    <i
                        className="fa-solid fa-triangle-exclamation mr-2"
                        aria-hidden="true"
                        style={{ color: errorType === 'rejected' ? '#856404' : '#842029' }}
                    />
                    {text}
                </div>
            </div>
        );
    }

    // ── Assistant bubble ─────────────────────────────────────────────────────
    const hasSummary = changesSummary && (
        (changesSummary.adds || 0) > 0 ||
        (changesSummary.updates || 0) > 0 ||
        (changesSummary.deletes || 0) > 0
    );

    const hasPreOpSnapshot = entry.preOpSnapshot !== undefined;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: 8,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, maxWidth: '90%' }}>
                {/* Robot icon */}
                <div
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: undone ? '#e9ecef' : '#6f42c1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: 2,
                    }}
                >
                    <i
                        className="fa-solid fa-robot"
                        aria-hidden="true"
                        style={{ color: '#fff', fontSize: '0.75rem' }}
                    />
                </div>

                {/* Bubble */}
                <div
                    style={{
                        backgroundColor: undone ? '#f8f9fa' : '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px 12px 12px 12px',
                        padding: '8px 12px',
                        fontSize: '0.85rem',
                        color: undone ? '#6c757d' : '#212529',
                        wordBreak: 'break-word',
                        opacity: undone ? 0.7 : 1,
                    }}
                >
                    {/* Main text (use as plain text — server sends i18n key resolved text or safe summary) */}
                    <div>{text}</div>

                    {/* Optional sanitized model prose summary — rendered as PLAIN TEXT (never HTML) */}
                    {modelSummary && !undone && (
                        <div style={{ marginTop: 4, color: '#495057' }}>
                            {modelSummary}
                        </div>
                    )}

                    {/* Changes summary */}
                    {hasSummary && !undone && (
                        <div style={{ marginTop: 4, color: '#6c757d', fontSize: '0.8rem' }}>
                            <FormattedMessage
                                id="TemplateAssistant.Reply.ChangesSummary"
                                values={{
                                    adds: changesSummary.adds || 0,
                                    updates: changesSummary.updates || 0,
                                    deletes: changesSummary.deletes || 0,
                                }}
                            />
                        </div>
                    )}

                    {/* Undo / undone state */}
                    {hasPreOpSnapshot && (
                        <div style={{ marginTop: 6 }}>
                            {undone ? (
                                <span
                                    style={{
                                        fontSize: '0.78rem',
                                        color: '#6c757d',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    <FormattedMessage id="TemplateAssistant.Undo.Done" />
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => onUndo(entry.id)}
                                    aria-label={intl.formatMessage({ id: 'TemplateAssistant.Undo.AriaLabel' })}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        color: '#0d6efd',
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        textUnderlineOffset: 2,
                                    }}
                                >
                                    <i className="fa-solid fa-rotate-left mr-1" aria-hidden="true" />
                                    <FormattedMessage id="TemplateAssistant.Undo.Label" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default injectIntl(AiMessageBubble);
